// src/pages/EditVehicle.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Divider,
  Autocomplete,
  Collapse,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router-dom";
import useDrivers from "../hooks/useDrivers";
import useVehicles from "../hooks/useVehicles";
import { updateVehicle, deleteVehicle } from "../api/vehicles";
import {
  getDocuments,
  uploadDocument,
  deleteDocument,
  downloadDocument,
} from "../api/documents";

export default function EditVehicle() {
  const { id } = useParams(); // vehicle._id from route /vehicles/:id
  const navigate = useNavigate();

  const { vehicles, loading: vehiclesLoading } = useVehicles();
  const { drivers, loading: driversLoading } = useDrivers();

  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(null);

  // Delete modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Validation + generic error state
  const [errors, setErrors] = useState({});
  const [genericError, setGenericError] = useState("");

  // Documents state (for this vehicle)
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const fileInputRef = useRef(null);

  // File delete dialog (type "confirm")
  const [fileDeleteOpen, setFileDeleteOpen] = useState(false);
  const [fileDeleteInput, setFileDeleteInput] = useState("");
  const [fileDeleteLoading, setFileDeleteLoading] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadType, setUploadType] = useState("");
  const [uploadExpiry, setUploadExpiry] = useState("");
  const [uploadErrors, setUploadErrors] = useState({});

  const vehicle = useMemo(
    () => vehicles.find((v) => v._id === id),
    [vehicles, id]
  );

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const clearFieldError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const toUnix = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor(d.getTime() / 1000);
  };

const pad = (n) => String(n).padStart(2, "0");

const fromUnixToDateInput = (ts) => {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  // Local date (DST-aware)
  return `${year}-${month}-${day}`;
};

const fromUnixToDatetimeInput = (ts) => {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  // Local datetime (DST-aware)
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

  const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-GB");
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("en-GB");
  };

  const loadDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const docs = await getDocuments("vehicle", id);
      setDocuments(docs || []);
    } catch (err) {
      console.error("Failed to load vehicle documents:", err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  // Initialise form when vehicle is loaded
  useEffect(() => {
    if (!vehicle) return;

    const phv = vehicle.phv || {};

    setForm({
      vrm: vehicle.vrm || "",
      vin: vehicle.vin || "",
      model_image: vehicle.model_image || "",
      date_first_registered: vehicle.date_first_registered || "",
      make: vehicle.make || "",
      make_model: vehicle.make_model || "",
      seating_capacity: vehicle.seating_capacity || "",
      body_type: vehicle.body_type || "",
      color: vehicle.color || "",
      fuel_type: vehicle.fuel_type || "",
      engine_capacity: vehicle.engine_capacity || "",
      transmission_type: vehicle.transmission_type || "",
      driver: vehicle.driver || "",

      phv_licence_number: phv.licence_number || "",
      phv_booking_date: fromUnixToDatetimeInput(phv.booking_test_timestamp),
      phv_interim_date: fromUnixToDatetimeInput(phv.interim_test_timestamp),
      phv_plate_expiry_date: fromUnixToDateInput(phv.plate_expiry_timestamp),
    });
  }, [vehicle]);

  // keep upload file name in sync
  useEffect(() => {
    setUploadFileName(uploadFile ? uploadFile.name.split(".")[0] : "");
  }, [uploadFile]);

  useEffect(() => {
    if (!id) return;
    loadDocuments();
  }, [id]);

  const selectedDriver =
    drivers.find((d) => d._id === form?.driver) || null;

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setErrors({});
    setGenericError("");
    try {
      const phv = {};
      if (form.phv_licence_number.trim()) {
        phv.licence_number = form.phv_licence_number.trim();
      }
      const bookingTs = toUnix(form.phv_booking_date);
      if (bookingTs) phv.booking_test_timestamp = bookingTs;
      else phv.booking_test_timestamp = null;

      const interimTs = toUnix(form.phv_interim_date);
      if (interimTs) phv.interim_test_timestamp = interimTs;
      else phv.interim_test_timestamp = null;

      const plateTs = toUnix(form.phv_plate_expiry_date);
      if (plateTs) phv.plate_expiry_timestamp = plateTs;
      else phv.plate_expiry_timestamp = null;

      const payload = {
        vrm: form.vrm.trim().toUpperCase(),
        vin: form.vin || "",
        model_image: form.model_image || "",
        date_first_registered: form.date_first_registered || "",
        make: form.make || "",
        make_model: form.make_model || "",
        seating_capacity: form.seating_capacity,
        body_type: form.body_type || "",
        color: form.color || "",
        fuel_type: form.fuel_type || "",
        engine_capacity: form.engine_capacity || "",
        transmission_type: form.transmission_type || "",
        driver: form.driver || null,
        phv,
      };

      await updateVehicle(id, payload);
      navigate("/vehicles");
    } catch (err) {
      console.error("Failed to update vehicle:", err);
      if (err.status === 400 && err.validationErrors) {
        setErrors(err.validationErrors || {});
      } else {
        setGenericError(
          "A	server error occurred while saving this vehicle. Please try again."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  // Delete handlers (now require typing "confirm")
  const handleOpenDelete = () => {
    setDeleteInput("");
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    if (deleteLoading) return;
    setDeleteOpen(false);
    setDeleteInput("");
  };

  const handleConfirmDelete = async () => {
    if (!form) return;
    setDeleteLoading(true);
    setGenericError("");
    try {
      await deleteVehicle(id);
      navigate("/vehicles");
    } catch (err) {
      console.error("Failed to delete vehicle:", err);
      setGenericError(
        "Failed to delete this vehicle. Please try again or contact support."
      );
    } finally {
      setDeleteLoading(false);
      setDeleteOpen(false);
    }
  };

  const deleteDisabled =
    !deleteInput.trim() ||
    deleteInput.trim().toLowerCase() !== "confirm";

  // Document actions

  const handleUploadClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);
    setUploadModalOpen(true);
  };

  const handleDownload = (docId) => {
    downloadDocument(docId);
  };

  const handleRequestDeleteDoc = (doc) => {
    setFileToDelete(doc);
    setFileDeleteInput("");
    setFileDeleteOpen(true);
  };

  const handleCloseFileDelete = () => {
    if (fileDeleteLoading) return;
    setFileDeleteOpen(false);
    setFileToDelete(null);
    setFileDeleteInput("");
  };

  const handleConfirmFileDelete = async () => {
    if (!fileToDelete) return;
    setFileDeleteLoading(true);
    try {
      await deleteDocument(fileToDelete._id);
      await loadDocuments();
    } catch (err) {
      console.error("Failed to delete document:", err);
      setGenericError("Failed to delete document. Please try again.");
    } finally {
      setFileDeleteLoading(false);
      setFileDeleteOpen(false);
      setFileToDelete(null);
      setFileDeleteInput("");
    }
  };

  const fileDeleteDisabled =
    !fileToDelete ||
    !fileDeleteInput.trim() ||
    fileDeleteInput.trim().toLowerCase() !== "confirm";

  if (vehiclesLoading || !form) {
    return (
      <Box sx={{ mt: 5, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", pb: 8 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5">Vehicle Details</Typography>
      </Box>

      {genericError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setGenericError("")}
        >
          {genericError}
        </Alert>
      )}

      {/* Vehicle Details */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
        }}
      >
        {/* Identification */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Identification
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="VRM"
                fullWidth
                value={form.vrm}
                onChange={(e) => {
                  updateField("vrm", e.target.value.toUpperCase());
                  clearFieldError("vrm");
                }}
                error={!!errors.vrm}
                helperText={errors.vrm}
              />
            ) : (
              <ReadOnlyField label="VRM" value={form.vrm} />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="VIN"
                fullWidth
                value={form.vin}
                onChange={(e) => {
                  updateField("vin", e.target.value);
                  clearFieldError("vin");
                }}
                error={!!errors.vin}
                helperText={errors.vin}
              />
            ) : (
              <ReadOnlyField label="VIN" value={form.vin} />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Date First Registered"
                fullWidth
                value={form.date_first_registered}
                onChange={(e) => {
                  updateField("date_first_registered", e.target.value);
                  clearFieldError("date_first_registered");
                }}
                error={!!errors.date_first_registered}
                helperText={errors.date_first_registered}
              />
            ) : (
              <ReadOnlyField
                label="Date First Registered"
                value={form.date_first_registered}
              />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Make"
                fullWidth
                value={form.make}
                onChange={(e) => {
                  updateField("make", e.target.value);
                  clearFieldError("make");
                }}
                error={!!errors.make}
                helperText={errors.make}
              />
            ) : (
              <ReadOnlyField label="Make" value={form.make} />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Make & Model"
                fullWidth
                value={form.make_model}
                onChange={(e) => {
                  updateField("make_model", e.target.value);
                  clearFieldError("make_model");
                }}
                error={!!errors.make_model}
                helperText={errors.make_model}
              />
            ) : (
              <ReadOnlyField label="Make & Model" value={form.make_model} />
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Appearance */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Appearance
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Colour"
                fullWidth
                value={form.color}
                onChange={(e) => {
                  updateField("color", e.target.value);
                  clearFieldError("color");
                }}
                error={!!errors.color}
                helperText={errors.color}
              />
            ) : (
              <ReadOnlyField label="Colour" value={form.color} />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Body Type"
                fullWidth
                value={form.body_type}
                onChange={(e) => {
                  updateField("body_type", e.target.value);
                  clearFieldError("body_type");
                }}
                error={!!errors.body_type}
                helperText={errors.body_type}
              />
            ) : (
              <ReadOnlyField label="Body Type" value={form.body_type} />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Seating Capacity"
                fullWidth
                value={form.seating_capacity}
                onChange={(e) => {
                  updateField("seating_capacity", e.target.value);
                  clearFieldError("seating_capacity");
                }}
                error={!!errors.seating_capacity}
                helperText={errors.seating_capacity}
              />
            ) : (
              <ReadOnlyField
                label="Seating Capacity"
                value={form.seating_capacity}
              />
            )}
          </Grid>

          {/* Model image preview */}
          <Grid item size={{ xs: 12, sm: 6 }}>
            {form.model_image ? (
              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  overflow: "hidden",
                  height: 140,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f7f7f7",
                }}
              >
                <Box
                  component="img"
                  src={form.model_image}
                  alt="Vehicle preview"
                  sx={{
                    maxHeight: "100%",
                    maxWidth: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                No preview available.
              </Typography>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Mechanical */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Mechanical
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Fuel Type"
                fullWidth
                value={form.fuel_type}
                onChange={(e) => {
                  updateField("fuel_type", e.target.value);
                  clearFieldError("fuel_type");
                }}
                error={!!errors.fuel_type}
                helperText={errors.fuel_type}
              />
            ) : (
              <ReadOnlyField label="Fuel Type" value={form.fuel_type} />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Engine Capacity"
                fullWidth
                value={form.engine_capacity}
                onChange={(e) => {
                  updateField("engine_capacity", e.target.value);
                  clearFieldError("engine_capacity");
                }}
                error={!!errors.engine_capacity}
                helperText={errors.engine_capacity}
              />
            ) : (
              <ReadOnlyField
                label="Engine Capacity"
                value={form.engine_capacity}
              />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Transmission Type"
                fullWidth
                value={form.transmission_type}
                onChange={(e) => {
                  updateField("transmission_type", e.target.value);
                  clearFieldError("transmission_type");
                }}
                error={!!errors.transmission_type}
                helperText={errors.transmission_type}
              />
            ) : (
              <ReadOnlyField
                label="Transmission Type"
                value={form.transmission_type}
              />
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* PHV / Licensing */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          PHV / Licensing
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            {editMode ? (
              <TextField
                label="Licence Number"
                fullWidth
                value={form.phv_licence_number}
                onChange={(e) => {
                  updateField("phv_licence_number", e.target.value);
                  clearFieldError("phv_licence_number");
                }}
                error={!!errors.phv_licence_number}
                helperText={errors.phv_licence_number}
              />
            ) : (
              <ReadOnlyField
                label="Licence Number"
                value={form.phv_licence_number}
              />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            {editMode ? (
              <TextField
                label="Plate Expiry Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.phv_plate_expiry_date}
                onChange={(e) => {
                  updateField("phv_plate_expiry_date", e.target.value);
                  clearFieldError("phv_plate_expiry_date");
                }}
                error={!!errors.phv_plate_expiry_date}
                helperText={errors.phv_plate_expiry_date}
              />
            ) : (
              <ReadOnlyField
                label="Plate Expiry Date"
                value={formatDate(form.phv_plate_expiry_date)}
              />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            {editMode ? (
              <TextField
                label="Booking Test Date & Time"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.phv_booking_date}
                onChange={(e) => {
                  updateField("phv_booking_date", e.target.value);
                  clearFieldError("phv_booking_date");
                }}
                error={!!errors.phv_booking_date}
                helperText={errors.phv_booking_date}
              />
            ) : (
              <ReadOnlyField
                label="Booking Test Date & Time"
                value={formatDateTime(form.phv_booking_date)}
              />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            {editMode ? (
              <TextField
                label="Interim Test Date & Time"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.phv_interim_date}
                onChange={(e) => {
                  updateField("phv_interim_date", e.target.value);
                  clearFieldError("phv_interim_date");
                }}
                error={!!errors.phv_interim_date}
                helperText={errors.phv_interim_date}
              />
            ) : (
              <ReadOnlyField
                label="Interim Test Date & Time"
                value={formatDateTime(form.phv_interim_date)}
              />
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Driver Assignment */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Driver Assignment
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            {editMode ? (
              <Autocomplete
                fullWidth
                options={drivers}
                loading={driversLoading}
                value={selectedDriver}
                onChange={(e, newValue) => {
                  updateField("driver", newValue ? newValue._id : "");
                  clearFieldError("driver");
                }}
                getOptionLabel={(option) =>
                  `${option.firstname || ""} ${
                    option.lastname || ""
                  }`.trim()
                }
                isOptionEqualToValue={(o, v) => o._id === v._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign Driver (optional)"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {driversLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    error={!!errors.driver}
                    helperText={errors.driver}
                  />
                )}
              />
            ) : (
              <ReadOnlyField
                label="Assigned Driver"
                value={
                  selectedDriver
                    ? `${selectedDriver.firstname || ""} ${
                        selectedDriver.lastname || ""
                      }`.trim()
                    : "Unassigned"
                }
              />
            )}
          </Grid>
        </Grid>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            mt: 2,
          }}
        >
          <Button
            color="error"
            variant="outlined"
            sx={{ textTransform: "none" }}
            onClick={handleOpenDelete}
          >
            Delete Vehicle
          </Button>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={() => navigate("/vehicles")}
            >
              Close
            </Button>

            <Collapse in={editMode}>
              <Button
                variant="contained"
                sx={{ textTransform: "none", ml: 2 }}
                onClick={handleSave}
                disabled={saving || !form.vrm}
              >
                {saving ? <CircularProgress size={22} /> : "Save Changes"}
              </Button>
            </Collapse>
          </Box>
        </Box>
      </Paper>

      {/* Documents panel */}
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Documents
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={handleUploadClick}
            >
              Upload
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>

          {documentsLoading ? (
            <CircularProgress size={18} />
          ) : (
            <>
              {(!documents || documents.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No documents uploaded for this vehicle.
                </Typography>
              )}

              <List dense>
                {documents.map((doc) => {
                  const name =
                    doc.filename || doc.originalName || "Document";
                  const typeLabel = doc.document_type
                    ? doc.document_type.replace(/_/g, " ")
                    : "Unknown";
                  const sizeLabel = doc.size
                    ? `${(doc.size / 1024).toFixed(1)} KB`
                    : "—";
                  const mimeLabel = doc.mime_type || "—";

                  return (
                    <ListItem
                      key={doc._id}
                      sx={{
                        px: 0,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          flexGrow: 1,
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{ fontWeight: 500 }}
                        >
                          {name}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={typeLabel}
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: "capitalize" }}
                          />

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {sizeLabel}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            •
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {mimeLabel}
                          </Typography>
                        </Box>
                      </Box>

                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDownload(doc._id)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          edge="end"
                          size="small"
                          sx={{ ml: 1 }}
                          onClick={() => handleRequestDeleteDoc(doc)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </Paper>
      </Box>

      {/* Delete confirmation dialog – now requires "confirm" */}
      <Dialog
        open={deleteOpen}
        onClose={handleCloseDelete}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Vehicle {form.vrm}</DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Typography variant="body2">
              This will permanently delete this vehicle and any associated PHV
              dates. To confirm, please type <strong>confirm</strong> in the box
              below. This action cannot be undone.
            </Typography>

            <TextField
              label='Type "confirm" to delete'
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteDisabled || deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : "Confirm Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete document dialog (type "confirm") */}
      <Dialog
        open={fileDeleteOpen}
        onClose={handleCloseFileDelete}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Delete Document
          {fileToDelete && fileToDelete.filename
            ? ` – ${fileToDelete.filename}`
            : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Typography variant="body2">
              This will permanently delete the selected document for this
              vehicle. To confirm, please type <strong>confirm</strong> in the
              box below.
            </Typography>
            <TextField
              label='Type "confirm" to delete'
              value={fileDeleteInput}
              onChange={(e) => setFileDeleteInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileDelete} disabled={fileDeleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmFileDelete}
            disabled={fileDeleteDisabled || fileDeleteLoading}
          >
            {fileDeleteLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Confirm Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload document dialog */}
      <Dialog
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setUploadErrors({});
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload Vehicle Document</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Typography variant="body2">
              Uploading: <strong>{uploadFile?.name}</strong>
            </Typography>

            <TextField
              required
              label="File Name"
              type="text"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={uploadFileName}
              onChange={(e) => setUploadFileName(e.target.value)}
              error={!!uploadErrors.filename}
              helperText={uploadErrors.filename}
            />

            <TextField
              required
              label="Document Type"
              select
              fullWidth
              value={uploadType}
              onChange={(e) => {
                setUploadType(e.target.value);
                setUploadErrors((p) => ({
                  ...p,
                  document_type: undefined,
                }));
              }}
              error={!!uploadErrors.document_type}
              helperText={uploadErrors.document_type}
            >
              <MenuItem value="v5c_original">V5C Original</MenuItem>
              <MenuItem value="v5c_redacted">V5C Redacted</MenuItem>
              <MenuItem value="licence_notice">License Notice</MenuItem>
              <MenuItem value="private_hire_vehicle_license">Private Hire Vehicle License</MenuItem>
              <MenuItem value="mot">MOT/Compliance</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <TextField
              label="Document Expiry Date (optional)"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={uploadExpiry}
              onChange={(e) => setUploadExpiry(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadModalOpen(false);
              setUploadErrors({});
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={async () => {
              const errs = {};
              if (!uploadFileName.trim()) {
                errs.filename = "File name is required";
              }
              if (!uploadType) {
                errs.document_type = "Document type is required";
              }

              if (Object.keys(errs).length > 0) {
                setUploadErrors(errs);
                return;
              }

              try {
                const formData = new FormData();
                formData.append("file", uploadFile);
                const originalParts = uploadFile.name.split(".");
                const ext =
                  originalParts.length > 1
                    ? originalParts[originalParts.length - 1]
                    : "";
                const finalName = ext
                  ? `${uploadFileName}.${ext}`
                  : uploadFileName;

                formData.append("filename", finalName);
                formData.append("entity_type", "vehicle");
                formData.append("entity_id", id);
                formData.append("document_type", uploadType);
                if (uploadExpiry) {
                  formData.append("expiry_date", uploadExpiry);
                }

                await uploadDocument(formData);

                setUploadModalOpen(false);
                setUploadErrors({});
                setUploadFile(null);
                setUploadFileName("");
                setUploadType("");
                setUploadExpiry("");

                await loadDocuments();
              } catch (err) {
                console.error("Failed to upload vehicle document:", err);
                setGenericError(
                  "Failed to upload document. Please try again."
                );
              }
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Edit FAB */}
      <Fab
        color={editMode ? "default" : "primary"}
        onClick={() => setEditMode((prev) => !prev)}
        sx={{
          position: "fixed",
          bottom: 32,
          right: 32,
          boxShadow: 4,
        }}
      >
        <EditIcon />
      </Fab>
    </Box>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography
        variant="caption"
        sx={{ textTransform: "uppercase", color: "text.secondary" }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}
